import type {
  MobileForensicCell,
  MobileForensicFamily,
  MobileForensicFamilyEntry,
  MobileForensicPlatformPost,
  MobileForensicRecord,
  MobileForensicSearchGroup,
  MobileForensicSummary,
  MobileForensicViewKind,
  MobileForensicViewModel,
} from '@tranquility-ai/theme-components';
import type { MobileColumnRender, MobileFrontEndRecordView, MobileUiConfigResponse } from '@/shared/mobile-service/api/cors-mobile-api';
import { resolveArtifactColumns } from '@/shared/mobile-service/lib/columns';
import { artifactFamily, familyRegistry } from './familyRegistry';
import type { UfdrChatSummary, UfdrExportData } from '../ufdr/legacy-types';
import { threadPost, threadSummary } from '../ufdr/toPlatformPost';

function cellFor(render: MobileColumnRender, value: unknown): MobileForensicCell {
  if (value == null || value === '') return { kind: render === 'bool' ? 'bool' : 'text' };
  switch (render) {
    case 'number':
      return { kind: 'number', value: typeof value === 'number' ? value : String(value) };
    case 'datetime':
      return { kind: 'datetime', value: String(value) };
    case 'duration':
      return typeof value === 'number' ? { kind: 'duration', seconds: value } : { kind: 'duration', value: String(value) };
    case 'bool':
      return { kind: 'bool', value: value === true || value === 'true' };
    case 'badge':
      return { kind: 'badge', value: String(value) };
    case 'url':
      return { kind: 'url', value: String(value) };
    case 'list':
      return { kind: 'list', value: Array.isArray(value) ? value.map(String) : [String(value)] };
    case 'phone_number':
      return { kind: 'phone_number', value: String(value) };
    case 'file_size':
      return typeof value === 'number' ? { kind: 'file_size', bytes: value } : { kind: 'file_size', value: String(value) };
    case 'direction':
      return { kind: 'direction', value: String(value) };
    case 'content':
      return { kind: 'content', value: typeof value === 'string' ? value : JSON.stringify(value) };
    default:
      return { kind: 'text', value: typeof value === 'string' ? value : JSON.stringify(value) };
  }
}

export function fileViewModel(exportData: UfdrExportData | undefined, title?: string): MobileForensicViewModel {
  return {
    mode: 'file',
    source: 'none',
    title: title || exportData?.title,
    families: [],
    tree: exportData?.tree ?? [],
    device: exportData?.device
      ? {
          model: exportData.device.model,
          os: exportData.device.os,
          extractedAt: exportData.device.extractedAt,
          extractionType: exportData.device.extractionType,
        }
      : undefined,
  };
}

export function processedViewModel(args: {
  title?: string;
  uiConfig: MobileUiConfigResponse | undefined;
  artifactType?: string;
  view?: MobileForensicViewKind;
  search?: string;
  filters?: NonNullable<MobileForensicViewModel['filters']>;
  recordId?: string;
  /** Host override. `files` opens the extraction tree instead of an artifact. */
  family?: MobileForensicFamily;
  nodeId?: string;
  tree?: MobileForensicViewModel['tree'];
  rows?: Array<Record<string, unknown>>;
  total?: number;
  page?: number;
  pageSize?: number;
  threads?: MobileForensicViewModel['threads'];
  activeThread?: MobileForensicViewModel['activeThread'];
  status?: MobileForensicViewModel['status'];
  recordPost?: MobileForensicPlatformPost;
}): MobileForensicViewModel {
  const families = familyRegistry(args.uiConfig);
  if (!families.some((entry) => entry.id === 'files')) {
    families.push({ id: 'files', types: [] });
  }
  const artifact = args.artifactType ? args.uiConfig?.artifacts[args.artifactType] : undefined;
  const family: MobileForensicFamily | undefined = artifact ? artifactFamily(artifact) : undefined;
  const views = artifact ? (artifact.front_end?.views ?? []).map((view) => view.name).filter((name): name is MobileForensicViewKind =>
    ['table', 'messages', 'map', 'gallery', 'timeline', 'cards', 'record'].includes(name),
  ) : [];
  const resolved = args.artifactType && args.uiConfig ? resolveArtifactColumns(args.artifactType, args.uiConfig) : null;
  const columns = (resolved?.columns ?? []).filter((column) => resolved?.defaultVisibleFields.includes(column.field) ?? true);
  return {
    mode: 'processed',
    source: 'mobileService',
    title: args.title,
    families,
    activeFamily: args.family === 'files' ? 'files' : family,
    activeTypeId: args.family === 'files' ? 'files' : args.artifactType,
    activeNodeId: args.nodeId,
    tree: args.tree,
    activeView: args.view ?? views[0],
    availableViews: views,
    search: args.search,
    filters: args.filters,
    threads: args.threads,
    activeThread: args.activeThread,
    status: args.status,
    table: columns.length
      ? {
          columns: columns.map((column) => ({ id: column.field, label: column.label, render: column.render, sortable: column.sortable })),
          rows: (args.rows ?? []).map((row, index) => ({
            id: String(row.mobileArtifactId ?? row.id ?? index),
            cells: Object.fromEntries(columns.map((column) => [column.field, cellFor(column.render, row[column.field])])),
          })),
          page: { page: args.page ?? 1, pageSize: args.pageSize ?? 50, total: args.total ?? args.rows?.length ?? 0 },
        }
      : undefined,
    summary: familySummary(families),
    trail: family && args.family !== 'files' ? threadTrail(args.threads, args.activeThread?.id) : undefined,
    record: args.family === 'files' ? undefined : buildServiceRecord(args),
  };
}

export type ExportViewSelection = {
  family?: MobileForensicFamily;
  typeId?: string;
  view?: MobileForensicViewKind;
  threadId?: string;
  search?: string;
  recordId?: string;
  nodeId?: string;
};

export type ExportViewLabels = {
  messages: string;
  contacts: string;
  media: string;
  timeline: string;
  files: string;
};

function includes(value: string | undefined, needle: string): boolean {
  return (value ?? '').toLowerCase().includes(needle);
}

/**
 * Chats, contacts, media, and timeline parsed from files inside the extraction.
 * Returns file mode when the export had nothing the assembler could read.
 */
export function exportViewModel(
  exportData: UfdrExportData | undefined,
  ui: ExportViewSelection = {},
  labels: ExportViewLabels,
): MobileForensicViewModel {
  const chats = exportData?.chats ?? [];
  const contacts = exportData?.contacts ?? [];
  const media = exportData?.media ?? [];
  const timeline = exportData?.timeline ?? [];
  if (chats.length === 0 && contacts.length === 0 && media.length === 0 && timeline.length === 0) {
    return fileViewModel(exportData, exportData?.title);
  }

  const apps = new Map<string, UfdrChatSummary[]>();
  for (const chat of chats) {
    const label = chat.app || 'Messages';
    const group = apps.get(label) ?? [];
    group.push(chat);
    apps.set(label, group);
  }

  const families: MobileForensicFamilyEntry[] = [];
  if (chats.length > 0 || timeline.length > 0) {
    const types = [...apps.entries()].map(([label, group]) => ({
      id: label,
      label,
      count: group.length,
      views: ['messages'] as MobileForensicViewKind[],
    }));
    if (timeline.length > 0) {
      types.push({ id: 'timeline', label: labels.timeline, count: timeline.length, views: ['timeline'] });
    }
    families.push({ id: 'message', count: chats.length, types });
  }
  if (contacts.length > 0) {
    families.push({
      id: 'contact',
      count: contacts.length,
      types: [{ id: 'contacts', label: labels.contacts, count: contacts.length, views: ['cards'] }],
    });
  }
  if (media.length > 0) {
    families.push({
      id: 'media',
      count: media.length,
      types: [{ id: 'media', label: labels.media, count: media.length, views: ['gallery'] }],
    });
  }
  families.push({ id: 'files', types: [{ id: 'files', label: labels.files, views: [] }] });

  const activeFamily = ui.family && families.some((entry) => entry.id === ui.family) ? ui.family : undefined;
  const family = families.find((entry) => entry.id === activeFamily);
  const activeTypeId = family?.types.some((type) => type.id === ui.typeId) ? ui.typeId : family?.types[0]?.id;
  const type = family?.types.find((entry) => entry.id === activeTypeId);
  const availableViews = type?.views ?? [];
  const activeView = availableViews.includes(ui.view as MobileForensicViewKind) ? ui.view : availableViews[0];
  const visibleChats = activeTypeId && apps.has(activeTypeId) ? apps.get(activeTypeId) ?? [] : chats;
  const open = activeFamily === 'message' && activeTypeId !== 'timeline'
    ? visibleChats.find((chat) => chat.id === ui.threadId) ?? visibleChats[0]
    : undefined;
  const needle = ui.search?.trim().toLowerCase() ?? '';
  const searchGroups: MobileForensicSearchGroup[] = [];
  if (needle) {
    const messageHits = chats.filter((chat) => includes(chat.peer, needle) || includes(chat.lastMessage, needle) || includes(chat.app, needle));
    if (messageHits.length > 0) {
      searchGroups.push({
        family: 'message',
        label: labels.messages,
        count: messageHits.length,
        hits: messageHits.map((chat) => ({ id: chat.id, title: chat.peer, typeId: chat.app || 'Messages', post: threadPost(chat) })),
      });
    }
    const contactHits = contacts.filter((contact) => includes(contact.name, needle));
    if (contactHits.length > 0) {
      searchGroups.push({
        family: 'contact',
        label: labels.contacts,
        count: contactHits.length,
        hits: contactHits.map((contact) => ({ id: contact.id, title: contact.name, typeId: 'contacts' })),
      });
    }
  }

  return {
    mode: 'processed',
    source: 'exportFiles',
    title: exportData?.title,
    device: exportData?.device
      ? {
          model: exportData.device.model,
          os: exportData.device.os,
          extractedAt: exportData.device.extractedAt,
          extractionType: exportData.device.extractionType,
        }
      : undefined,
    families,
    activeFamily,
    activeTypeId,
    activeView,
    availableViews,
    search: needle ? ui.search : undefined,
    searchGroups: needle ? searchGroups : undefined,
    summary: exportSummary(apps, contacts, timeline),
    trail: open && activeFamily === 'message' && activeTypeId !== 'timeline'
      ? [{ id: `thread:${open.id}`, label: open.peer || open.app || labels.messages }]
      : undefined,
    record: contactRecord(contacts.find((contact) => contact.id === ui.recordId), chats, labels.messages),
    activeNodeId: ui.nodeId,
    threads: activeFamily === 'message' && activeTypeId !== 'timeline' ? visibleChats.map(threadSummary) : undefined,
    activeThread: open?.messages?.length ? threadPost(open) : undefined,
    contacts: contacts.map((contact) => ({
      id: contact.id,
      name: contact.name,
      identifiers: [...(contact.phoneNumbers ?? []), ...(contact.emails ?? [])],
      meta: contact.source,
    })),
    gallery: media.map((item) => ({
      id: item.id,
      label: item.label,
      mediaType: item.mediaType,
      src: item.src,
      thumbnailUrl: item.thumbnailUrl,
      timestamp: item.timestamp,
    })),
    timeline: timeline.map((event) => ({
      id: event.id,
      timestamp: event.timestamp,
      title: event.description,
      lane: event.category,
    })),
    tree: exportData?.tree ?? [],
  };
}

function familySummary(families: MobileForensicFamilyEntry[]): MobileForensicSummary | undefined {
  const messages = families.find((family) => family.id === 'message');
  const contacts = families.find((family) => family.id === 'contact');
  const topApps = (messages?.types ?? []).slice(0, 3).map((type) => ({
    id: type.id,
    label: type.label,
    family: 'message' as const,
    typeId: type.id,
    count: type.count,
  }));
  const topContacts = (contacts?.types ?? []).slice(0, 3).map((type) => ({
    id: type.id,
    label: type.label,
    family: 'contact' as const,
    typeId: type.id,
    count: type.count,
  }));
  if (topApps.length === 0 && topContacts.length === 0) return undefined;
  return {
    topApps: topApps.length > 0 ? topApps : undefined,
    topContacts: topContacts.length > 0 ? topContacts : undefined,
  };
}

function threadTrail(
  threads: MobileForensicViewModel['threads'],
  threadId: string | undefined,
): MobileForensicViewModel['trail'] {
  const thread = threads?.find((entry) => entry.id === threadId);
  if (!thread) return undefined;
  return [{ id: `thread:${thread.id}`, label: thread.title }];
}

function buildServiceRecord(args: {
  artifactType?: string;
  uiConfig: MobileUiConfigResponse | undefined;
  rows?: Array<Record<string, unknown>>;
  recordId?: string;
  recordPost?: MobileForensicPlatformPost;
}): MobileForensicRecord | undefined {
  const artifact = args.artifactType ? args.uiConfig?.artifacts[args.artifactType] : undefined;
  const recordView = artifact?.front_end?.views?.find(
    (view): view is MobileFrontEndRecordView => view.name === 'record',
  );
  const relations = recordView?.related;
  const selected = args.recordId
    ? args.rows?.find((row) => String(row.mobileArtifactId ?? row.id) === args.recordId)
    : undefined;
  if (!selected && !args.recordPost) return undefined;
  const related = relations?.flatMap((relation) => {
    if (!relation.graphql_type) return [];
    const value = selected?.[relation.field];
    const rows = Array.isArray(value)
      ? value.map((item, index) => ({ id: relatedRowId(item, index), cells: {} }))
      : [];
    if (rows.length === 0) return [];
    return [{ id: relation.field, label: relation.label || relation.field, typeId: relation.graphql_type, rows }];
  });
  const provenance = selected ? stringField(selected, 'source_path') ?? stringField(selected, 'path') : undefined;
  return {
    id: args.recordId || args.artifactType || 'record',
    title: selected ? stringField(selected, 'name') ?? stringField(selected, 'title') : undefined,
    sections: [],
    related,
    provenance,
    post: args.recordPost,
  };
}

function relatedRowId(item: unknown, index: number): string {
  if (typeof item === 'object' && item && 'id' in item && item.id != null) return String(item.id);
  if (typeof item === 'string' || typeof item === 'number') return String(item);
  return String(index);
}

function stringField(row: Record<string, unknown>, key: string): string | undefined {
  const value = row[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function exportSummary(
  apps: Map<string, UfdrChatSummary[]>,
  contacts: Array<{ id: string; name: string }>,
  timeline: Array<{ timestamp: string }>,
): MobileForensicSummary {
  const stamps = timeline.map((event) => event.timestamp).filter(Boolean).sort();
  const topApps = [...apps.entries()]
    .sort((left, right) => right[1].length - left[1].length)
    .slice(0, 3)
    .map(([label, group]) => ({
      id: label,
      label,
      family: 'message' as const,
      typeId: label,
      count: group.length,
    }));
  const topContacts = contacts.slice(0, 3).map((contact) => ({
    id: contact.id,
    label: contact.name,
    family: 'contact' as const,
    typeId: 'contacts',
  }));
  return {
    dateRange: stamps.length > 1 ? `${stamps[0]} ${stamps[stamps.length - 1]}` : stamps[0],
    topApps: topApps.length > 0 ? topApps : undefined,
    topContacts: topContacts.length > 0 ? topContacts : undefined,
  };
}

function contactRecord(
  contact: { id: string; name: string; source?: string; chatIds?: string[] } | undefined,
  chats: UfdrChatSummary[],
  messagesLabel: string,
): MobileForensicRecord | undefined {
  if (!contact) return undefined;
  const linked = chats.filter((chat) => contact.chatIds?.includes(chat.id));
  const byApp = new Map<string, UfdrChatSummary[]>();
  for (const chat of linked) {
    const app = chat.app || messagesLabel;
    const group = byApp.get(app) ?? [];
    group.push(chat);
    byApp.set(app, group);
  }
  return {
    id: contact.id,
    title: contact.name,
    sections: [],
    provenance: contact.source,
    related: [...byApp.entries()].map(([app, group]) => ({
      id: app,
      label: app,
      typeId: app,
      rows: group.map((chat) => ({ id: chat.id, cells: {} })),
    })),
  };
}
